'use client'

import { useTranslation } from '@/hooks/client/use-translation'
import SvgCheck from '@/icons/regular/Check'
import SvgPlus from '@/icons/regular/Plus'
import { tw } from '@/lib/utils'

interface CompareButtonProps {
  selected: boolean
  /** Four already picked and this one is not among them. */
  disabled: boolean
  name: string
  onToggle: () => void
}

// The labeled add-to-comparison control (replaces the old unlabeled circle).
// Picked = navy, matching the tray it lands in.
export const CompareButton = ({ selected, disabled, name, onToggle }: CompareButtonProps) => {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      data-tracking="ignore"
      aria-pressed={selected}
      aria-disabled={disabled || undefined}
      aria-label={
        selected
          ? t('client.compare.removeFromCompare', 'Remove {{name}} from comparison', { name })
          : t('directory.row.addToCompare', 'Add {{name}} to comparison', { name })
      }
      title={disabled ? t('directory.dock.full', 'Four picked. Remove one to add another.') : undefined}
      onClick={() => {
        if (!disabled) {
          onToggle()
        }
      }}
      className={tw(
        'focus-visible:outline-lapis inline-flex min-h-[44px] items-center gap-2 rounded-xl border px-4 text-[14px] font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2',
        selected && 'border-navy bg-navy hover:bg-navy/90 cursor-pointer text-white',
        !selected && !disabled && 'border-navy/25 text-navy hover:border-navy cursor-pointer bg-white',
        !selected && disabled && 'border-navy/10 text-navy/40 cursor-not-allowed bg-white'
      )}
    >
      {selected ? (
        <SvgCheck className="h-3.5 w-3.5 fill-current" aria-hidden />
      ) : (
        <SvgPlus className="h-3.5 w-3.5 fill-current" aria-hidden />
      )}
      {selected ? t('directory.row.added', 'Added') : t('directory.row.compare', 'Compare')}
    </button>
  )
}
